import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Loader2, Building2, FileText, Banknote, Smartphone, User, Wallet } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { formatDateToDdmmyyyy, numberToWords } from '../../../utils/format';
import { adminOrderService, api, couponService, marketingService, ocrService } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import { API_ENDPOINTS } from '../../../config/api';
import { storageEnv } from '../../../config/firebase';
import { AdminCreateOrderRequest } from '../../../types';
import Snackbar from '../../common/Snackbar';
import ImageViewerModal from '../../common/ImageViewerModal';
import MaskedMobile from '../../common/MaskedMobile';
import Select from '../../common/Select';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { ImagePreviewModal } from './CreateOrderModal';
import {
    PaymentMethodType, MultipleSubMode, TransferMode,
    UploadState, emptyUpload,
    uploadToFirebase,
    applyAutoFillValues,
    clearAutoFilledFields,
    isValidDdmmyyyy,
    extractTextFromExistingImage,
    fieldsForPaymentType,
    PaymentAutoFillValues,
} from './paymentFormUtils';
import { activeOnly } from '../../../utils/activeUsers';
import { personOption, staffFilterValue } from '../../common/StaffFilterOption';

const PAYMENT_METHOD_CHIPS: { key: PaymentMethodType; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={15} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={15} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={15} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={15} /> },
];


const UserCreateOrderPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const canCreateOrder = usePermissions().canAction('users', 'create_order', true);
    const [utrDuplicate, setUtrDuplicate] = useState(false);

    const adminMobile = useAppSelector((state: RootState) => state.auth.adminMobile || state.users.adminProfile?.mobile || '');
    const adminName = useAppSelector((state: RootState) => state.auth.adminName || state.users.adminProfile?.first_name || '');
    const adminRole = useAppSelector((state: RootState) => state.users.adminProfile?.role || state.auth.adminRole || '');

    const userName: string = (location.state as any)?.userName || '';
    const userCountryCode: string | undefined = (location.state as any)?.countryCode;
    const userId: string | undefined = (location.state as any)?.userId;

    // Product / Farm state
    const [products, setProducts] = useState<{ id: string; name?: string; price: number }[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [farms, setFarms] = useState<{ id: string; location?: string }[]>([]);
    const [loadingFarms, setLoadingFarms] = useState(false);
    const [marketingStaff, setMarketingStaff] = useState<{ mobile: string; first_name?: string; last_name?: string; role?: string }[]>([]);
    const [loadingMarketingStaff, setLoadingMarketingStaff] = useState(false);
    const [orderOnboardedByMobile, setOrderOnboardedByMobile] = useState('');

    // Create order state
    const [breedId, setBreedId] = useState('');
    const [numUnits, setNumUnits] = useState<number>(1);
    const [baseUnitCost, setBaseUnitCost] = useState<number>(0);
    const [farmId, setFarmId] = useState('');
    const [createdDate] = useState(new Date().toISOString().split('T')[0]);

    // Payment state
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>('BANK_TRANSFER');
    const [txDate, setTxDate] = useState('');
    const [transferMode, setTransferMode] = useState<TransferMode>('NEFT');
    const [utrNumber, setUtrNumber] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankName, setBankName] = useState('');
    const [cashierName, setCashierName] = useState('');
    const [cashierPhone, setCashierPhone] = useState('');
    const [multipleSubMode] = useState<MultipleSubMode>('BANK_TRANSFER');
    const [txAmount, setTxAmount] = useState<string | number>('');
    const [paidBy, setPaidBy] = useState<string>('');
    const [couponCode, setCouponCode] = useState<string>('');
    // Extra the customer paid over the amount due — sent as its own SURPLUS transaction
    // alongside the real payment, so it can be credited to their Animalkart wallet.
    const [surplusAmount, setSurplusAmount] = useState<string>('');
    const [surplusComment, setSurplusComment] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    // Upload state
    const [screenshotUpload, setScreenshotUpload] = useState<UploadState>(emptyUpload());
    const [voucherUpload, setVoucherUpload] = useState<UploadState>(emptyUpload());
    const [chequeFrontUpload, setChequeFrontUpload] = useState<UploadState>(emptyUpload());
    const [chequeBackUpload, setChequeBackUpload] = useState<UploadState>(emptyUpload());
    const [upiScreenshotUpload, setUpiScreenshotUpload] = useState<UploadState>(emptyUpload());

    // OCR text extracted from whichever upload is "active" for the currently selected
    // payment method — drives the inline image+OCR+fields panel below.
    const [ocrText, setOcrText] = useState<string | null>(null);
    const [ocrLoading, setOcrLoading] = useState(false);

    // Coins state
    const [availableCoins, setAvailableCoins] = useState<number | null>(null);
    const [loadingCoins, setLoadingCoins] = useState(false);
    const [coinsToRedeem, setCoinsToRedeem] = useState<string>('');

    // Wallet state
    const [availableWallet, setAvailableWallet] = useState<number | null>(null);
    const [loadingWallet, setLoadingWallet] = useState(false);
    const [walletAmountToUse, setWalletAmountToUse] = useState<string>('');

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    // Zoomed-in view of the active upload/cheque-back image — opened from either the
    // Image Upload or Paste URL tab, so both get the same "View" affordance.
    const [zoomUrl, setZoomUrl] = useState<string | null>(null);

    // Fields explicitly Verified via the Extracted Details panel — Create Order is gated on
    // every required field for the active mode being Verified before it can submit.
    const [verifiedFields, setVerifiedFields] = useState<Set<keyof PaymentAutoFillValues>>(new Set());

    const totalCost = baseUnitCost * numUnits;
    const anyUploading = screenshotUpload.uploading || voucherUpload.uploading ||
        chequeFrontUpload.uploading || chequeBackUpload.uploading || upiScreenshotUpload.uploading;

    const effectiveCoinsRedeemed = paymentMethod === 'COINS_REDEEM'
        ? Math.min(Number(coinsToRedeem) || 0, totalCost)
        : 0;
    const effectiveWalletUsed = paymentMethod === 'WALLET'
        ? Math.min(Number(walletAmountToUse) || 0, totalCost)
        : 0;
    const remainingAfterCoins = Math.max(totalCost - effectiveCoinsRedeemed - effectiveWalletUsed, 0);
    const coinsFullyCover = paymentMethod === 'COINS_REDEEM' && totalCost > 0 && remainingAfterCoins === 0;
    // Gates Create Order: every field the Extracted Details panel shows for the active mode
    // must be Verified before submitting — mirrors Edit Transaction's same requirement.
    const isRealPaymentMode = !!paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' && paymentMethod !== 'COUPON';
    // Whichever upload slot matches the currently selected method — the single "proof image"
    // the payment panel shows and lets the admin upload/re-upload (mirrors Edit Transaction).
    const isCashDeposit = paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT';
    // Cash Deposit shows Paid By/UTR/Bank Name too, but they're optional, so they're excluded here.
    const optionalForCashDeposit = ['paidBy', 'utrNumber', 'accountHolderName', 'bankName'];
    const requiredFieldKeys: (keyof PaymentAutoFillValues)[] = isRealPaymentMode
        ? (['txAmount', 'txDate', ...fieldsForPaymentType(paymentMethod || '', paidBy).map(f => f.key)] as (keyof PaymentAutoFillValues)[])
            .filter(k => !(isCashDeposit && optionalForCashDeposit.includes(k)))
        : [];
    const allFieldsVerified = !isRealPaymentMode || requiredFieldKeys.every(k => verifiedFields.has(k));
    const activeUpload = paymentMethod === 'CASH' ? voucherUpload
        : paymentMethod === 'CHEQUE' ? chequeFrontUpload
            : paymentMethod === 'UPI' ? upiScreenshotUpload
                : screenshotUpload;
    const setActiveUpload = paymentMethod === 'CASH' ? setVoucherUpload
        : paymentMethod === 'CHEQUE' ? setChequeFrontUpload
            : paymentMethod === 'UPI' ? setUpiScreenshotUpload
                : setScreenshotUpload;
    const activeUploadErrorKey = paymentMethod === 'CASH' ? 'voucherUpload'
        : paymentMethod === 'CHEQUE' ? 'chequeFrontUpload'
            : paymentMethod === 'UPI' ? 'upiScreenshotUpload'
                : 'screenshotUpload';
    const activeUploadLabel = paymentMethod === 'CASH' ? 'Voucher Image'
        : paymentMethod === 'CHEQUE' ? 'Cheque Front Image'
            : paymentMethod === 'UPI' ? 'Payment Screenshot'
                : isCashDeposit ? 'Deposit Slip / Screenshot'
                    : 'Payment Document';

    // Runs OCR against whichever image is currently active — a freshly picked file goes
    // straight to the OCR service; a pre-existing (already-uploaded) image is fetched via
    // the CORS proxy first, since there's no local File object for it.
    useEffect(() => {
        const url = activeUpload.url;
        if (!url) { setOcrText(null); setOcrLoading(false); return; }
        let cancelled = false;
        setOcrLoading(true);
        const run = activeUpload.file
            ? ocrService.extractText(activeUpload.file).then(r => r?.text || null)
            : extractTextFromExistingImage(url);
        run
            .then(text => { if (!cancelled) setOcrText(text); })
            .catch(() => { if (!cancelled) setOcrText(null); })
            .finally(() => { if (!cancelled) setOcrLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUpload.url, activeUpload.file]);

    const updateError = (field: string) => {
        setErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };
    // A field was edited (not explicitly Verified) — its previous Verified state and any
    // stale error from a prior failed submit no longer apply to the new value.
    const unverify = (key: string) => setVerifiedFields(prev => {
        if (!prev.has(key as keyof PaymentAutoFillValues)) return prev;
        const next = new Set(prev); next.delete(key as keyof PaymentAutoFillValues); return next;
    });
    const onFieldEdited = (key: keyof PaymentAutoFillValues) => { updateError(key); unverify(key); };

    // Clears every payment-method-specific field — called whenever the payment method or
    // MULTIPLE sub-mode changes so details from the previous selection never carry over.
    const resetPaymentSubFields = () => {
        setTransferMode('NEFT');
        setUtrNumber('');
        setAccountHolderName('');
        setBankName('');
        setCashierName('');
        setCashierPhone('');
        setPaidBy('');
        setCouponCode('');
        setCoinsToRedeem('');
        setWalletAmountToUse('');
        setSurplusAmount('');
        setSurplusComment('');
        setComment('');
        setScreenshotUpload(emptyUpload());
        setVoucherUpload(emptyUpload());
        setChequeFrontUpload(emptyUpload());
        setChequeBackUpload(emptyUpload());
        setUpiScreenshotUpload(emptyUpload());
        setVerifiedFields(new Set());
        setErrors({});
    };

    const fetchProducts = useCallback(async () => {
        setLoadingProducts(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getProducts());
            const data = response.data;
            const productList = (data?.products || data || [])
                .filter((p: any) => p.inStock === true)
                .map((p: any) => ({
                    id: p.id, name: p.name || p.breed || p.id, price: parseFloat(p.price || 0) * 2,
                }));
            setProducts(productList);
            if (productList.length > 0) {
                setBreedId(productList[0].id);
                setBaseUnitCost(productList[0].price);
            }
        } catch { setProducts([]); } finally { setLoadingProducts(false); }
    }, []);

    const fetchFarms = useCallback(async () => {
        setLoadingFarms(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getFarms(), { params: { status: 'ACTIVE' } });
            const data = response.data;
            const farmList = (data?.farms || data || [])
                .filter((f: any) => !f.status || f.status === 'ACTIVE')
                .map((f: any) => ({ id: f.id, location: f.location || f.address || f.name || f.id }));
            setFarms(farmList);
            if (farmList.length > 0) setFarmId(farmList[0].id);
        } catch { setFarms([]); } finally { setLoadingFarms(false); }
    }, []);

    const fetchMarketingStaff = useCallback(async () => {
        setLoadingMarketingStaff(true);
        try {
            const response = await marketingService.listStaff(adminMobile);
            setMarketingStaff(response?.staff || []);
        } catch {
            setMarketingStaff([]);
        } finally {
            setLoadingMarketingStaff(false);
        }
    }, [adminMobile]);

    const fetchAvailableCoins = useCallback(async (mob: string) => {
        setLoadingCoins(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getCoinTransaction(mob));
            if (response?.data?.statuscode === 200 && response?.data?.stats) {
                setAvailableCoins(Math.floor(response.data.stats.remaining_coins ?? 0));
            } else {
                setAvailableCoins(0);
            }
        } catch { setAvailableCoins(0); } finally { setLoadingCoins(false); }
    }, []);

    const fetchAvailableWallet = useCallback(async (mob: string) => {
        setLoadingWallet(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getMoneyTransaction(mob));
            if (response?.data?.statuscode === 200 && response?.data?.stats) {
                setAvailableWallet(Math.floor(response.data.stats.remaining_money ?? 0));
            } else {
                setAvailableWallet(0);
            }
        } catch { setAvailableWallet(0); } finally { setLoadingWallet(false); }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchFarms();
        fetchMarketingStaff();
        if (mobile) {
            fetchAvailableCoins(mobile);
            fetchAvailableWallet(mobile);
        }
    }, [fetchProducts, fetchFarms, fetchMarketingStaff, fetchAvailableCoins, fetchAvailableWallet, mobile]);

    const handleFileUpload = async (
        file: File,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        pathSuffix: string,
        errorKey?: string,
        folder: string = 'payments',
    ) => {
        if (!mobile) return;
        if (errorKey) updateError(errorKey);
        setUpload(prev => ({ ...prev, file, url: '', uploading: true, error: '', progress: 0 }));
        try {
            let finalFile = file;
            if (file.type.startsWith('image/')) {
                try {
                    finalFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                } catch { /* use original */ }
            }
            const path = `animalkart/${storageEnv}/${userId || mobile}/${folder}/${Date.now()}_${pathSuffix}_${file.name}`;
            const url = await uploadToFirebase(finalFile, path, pct => setUpload(prev => ({ ...prev, progress: pct })));
            setUpload(prev => ({ ...prev, file: finalFile, url, uploading: false }));
        } catch {
            setUpload(prev => ({ ...prev, uploading: false, error: 'Upload failed. Please try again.' }));
        }
    };

    // Seeds an upload tile with a pasted image URL directly — no file, no Firebase upload.
    // OCR against it runs the same way it does for a pre-existing (edit-mode) image.
    const handleUrlSubmit = async (
        url: string,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        errorKey?: string,
    ) => {
        if (errorKey) updateError(errorKey);
        
        if (url.includes('firebasestorage.googleapis.com')) {
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
            return;
        }

        try {
            setUpload(prev => ({ ...prev, file: null, url: '', uploading: true, error: '', progress: 0 }));
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const fileName = "pasted_image" + (blob.type.includes('png') ? '.png' : '.jpg');
            const file = new File([blob], fileName, { type: blob.type });
            
            const pathSuffix = errorKey === 'chequeBackUpload' ? 'cheque_back' : 'proof';
            handleFileUpload(file, setUpload, pathSuffix, errorKey);
        } catch (err: any) {
            console.warn("Could not download pasted image for Firebase upload (likely CORS):", err);
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
        }
    };

    const buildTransaction = () => {
        if (!paymentMethod || paymentMethod === 'NONE' || paymentMethod === 'COUPON' || paymentMethod === 'COINS_REDEEM') return undefined;
        const dateStr = txDate || undefined;

        if (paymentMethod === 'WALLET') {
            return {
                paymentMethod: 'MONEY_WALLET',
                amount: Number(walletAmountToUse) || 0,
                transactionDate: new Date().toISOString(),
            };
        }

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'COUPON') return undefined;
            const amount = Number(txAmount) || 0;
            const base: any = {
                paymentMethod: multipleSubMode,
                amount,
                transactionDate: dateStr,
                paid_by: paidBy,
                ...(comment.trim() ? { comment: comment.trim() } : {}),
            };
            if (multipleSubMode === 'BANK_TRANSFER') {
                return {
                    ...base,
                    transferMode,
                    ...(utrNumber.trim() ? { utrNumber } : {}),
                    ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                    ...(bankName.trim() ? { bankName } : {}),
                    paymentScreenshotUrl: screenshotUpload.url,
                };
            }
            if (multipleSubMode === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
            if (multipleSubMode === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
            if (multipleSubMode === 'UPI') return { ...base, utrNumber, accountHolderName, paymentScreenshotUrl: upiScreenshotUpload.url };
            return base;
        }

        const amt = (txAmount !== '' && txAmount !== undefined && txAmount !== null) ? Number(txAmount) : (remainingAfterCoins || totalCost);
        const base: any = {
            paymentMethod,
            amount: amt,
            transactionDate: dateStr,
            paid_by: paidBy,
            ...(comment.trim() ? { comment: comment.trim() } : {}),
        };
        if (paymentMethod === 'BANK_TRANSFER') {
            return {
                ...base,
                transferMode,
                ...(utrNumber.trim() ? { utrNumber } : {}),
                ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                ...(bankName.trim() ? { bankName } : {}),
                paymentScreenshotUrl: screenshotUpload.url,
            };
        }
        if (paymentMethod === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
        if (paymentMethod === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
        if (paymentMethod === 'UPI') return { ...base, utrNumber, accountHolderName: paidBy !== 'Self' ? accountHolderName : undefined, paymentScreenshotUrl: upiScreenshotUpload.url };
        return base;
    };

    const validatePayment = (): Record<string, string> => {
        const errs: Record<string, string> = {};
        if (!paymentMethod) { errs.paymentMethod = 'Select a payment method'; return errs; }
        if (paymentMethod === 'COINS_REDEEM') {
            if (!Number(coinsToRedeem) || Number(coinsToRedeem) <= 0) errs.coinsToRedeem = 'Enter coins to redeem';
            return errs;
        }
        if (paymentMethod === 'WALLET') {
            const walletAmt = Number(walletAmountToUse) || 0;
            if (!walletAmt || walletAmt <= 0) errs.walletAmountToUse = 'Enter wallet amount to use';
            else if (availableWallet !== null && walletAmt > availableWallet) errs.walletAmountToUse = `Only ₹${availableWallet.toLocaleString('en-IN')} available in wallet`;
            return errs;
        }
        if (paymentMethod === 'NONE' || coinsFullyCover) return errs;
        const isCoupon = paymentMethod === 'COUPON' || (paymentMethod === 'MULTIPLE' && multipleSubMode === 'COUPON');
        if (!txDate && !isCoupon) {
            errs.txDate = 'Transaction date is required';
        } else if (txDate && !isCoupon && !isValidDdmmyyyy(txDate)) {
            errs.txDate = 'Enter date in DD-MM-YYYY format';
        }
        if (!isCoupon) {
            const amountVal = Number(txAmount) || 0;
            const maxAmount = remainingAfterCoins;
            if (!amountVal || amountVal <= 0) errs.txAmount = 'Enter payment amount';
            else if (amountVal > maxAmount) errs.txAmount = `Amount exceeds remaining (₹${maxAmount.toLocaleString('en-IN')})`;
        } else {
            if (!couponCode.trim()) errs.couponCode = 'Coupon code is required';
        }

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode !== 'CASH_DEPOSIT') {
                    if (paidBy !== 'Self' && !accountHolderName.trim()) errs.accountHolderName = 'Account holder name is required';
                    if (!bankName.trim()) errs.bankName = 'Bank name is required';
                }
            }
            if (multipleSubMode === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (multipleSubMode === 'CHEQUE') {
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
            }
            if (multipleSubMode === 'UPI') {
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        } else {
            if (paymentMethod === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode !== 'CASH_DEPOSIT') {
                    if (paidBy !== 'Self' && !accountHolderName.trim()) errs.accountHolderName = 'Account holder name is required';
                    if (!bankName.trim()) errs.bankName = 'Bank name is required';
                }
            }
            if (paymentMethod === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (paymentMethod === 'CHEQUE') {
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
            }
            if (paymentMethod === 'UPI') {
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        }
        return errs;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mobile) return;
        const errs: Record<string, string> = {};
        if (!breedId) errs.breedId = 'Please select a breed';
        if (!numUnits || numUnits < 1 || numUnits > 999) errs.numUnits = 'Enter units between 1 and 999';
        if (!farmId) errs.farmId = 'Please select a farm location';
        Object.assign(errs, validatePayment());
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setLoading(true);
        try {
            const isCoupon = paymentMethod === 'COUPON';
            const isCoins = paymentMethod === 'COINS_REDEEM';
            const isWallet = paymentMethod === 'WALLET';
            const mainTx = (isCoins || coinsFullyCover || isCoupon) ? undefined : buildTransaction();
            const surplusNum = Number(surplusAmount) || 0;
            const transactions = mainTx
                ? [mainTx, ...(surplusNum > 0 ? [{
                    paymentMethod: 'SURPLUS' as const,
                    amount: surplusNum,
                    transactionDate: txDate || undefined,
                    comment: surplusComment.trim() || `Customer over-paid by ${surplusNum}`,
                }] : [])]
                : undefined;
            const payload: AdminCreateOrderRequest = {
                userId: mobile, breedId, numUnits, baseUnitCost,
                withCpf: false, cpfUnitCost: 0, farmId: farmId || undefined,
                paymentMode: (isCoins && coinsFullyCover) ? 'COINS_REDEEM' : 'MANUAL_PAYMENT',
                paymentUpdatedByMobile: adminMobile,
                paymentUpdatedByName: adminName,
                paymentUpdatedByRole: adminRole,
                orderCreatedDate: createdDate || undefined,
                orderOnboardedByMobile: orderOnboardedByMobile || undefined,
                paymentType: (isCoins || coinsFullyCover || paymentMethod === 'NONE' || isCoupon)
                    ? undefined
                    : isWallet
                        ? 'MONEY_WALLET'
                        : (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT')
                            ? 'CASH_DEPOSIT'
                            : (paymentMethod || undefined),
                coinsRedeemed: effectiveCoinsRedeemed || 0,
                transactions,
                paid_by: paidBy,
                comment: comment.trim() || undefined,
            } as any;
            const result = await adminOrderService.createOrder(payload, adminMobile);
            if (result.error) {
                const errorMessage = result.error;
                if (/marketing exec|onboarded by someone outside your team/i.test(errorMessage)) {
                    setErrors(prev => ({ ...prev, orderOnboardedByMobile: errorMessage }));
                }
                setSnackbar({ message: errorMessage, type: 'error' });
                return;
            }
            if (isCoupon) {
                const createdOrderId = result.data?.id || result.data?.order?.id || result.data?.orderId;
                if (!createdOrderId) {
                    setSnackbar({ message: 'Order created, but failed to find order ID to apply coupon.', type: 'error' });
                    return;
                }
                const couponRes = await couponService.applyCoupon({ code: couponCode.trim(), order_id: createdOrderId }, adminMobile || '');
                if (couponRes.error) {
                    setSnackbar({ message: `Order created, but failed to apply coupon: ${couponRes.error}`, type: 'error' });
                    return;
                }
            }
            const newOrderId = result.data?.order?.id || result.data?.id || result.data?.orderId;
            const remaining = result.data?.remaining ?? 0;

            // Always hand off to Update Payment — it covers adding nominee/bank details for
            // both a still-partial order and one that came in fully paid.
            if (newOrderId && mobile) {
                setSnackbar({ message: remaining > 0 ? 'Order created! Add remaining payment.' : 'Order created successfully!', type: 'success' });
                setTimeout(() => navigate(
                    `/users/customers/${mobile}/orders/${encodeURIComponent(newOrderId)}/add-payment`,
                    { state: { fromPage: 'Users', userName, countryCode: userCountryCode, userId, orderTotal: result.data?.totalCost ?? totalCost, fromCreateOrder: true } }
                ), 800);
            } else {
                setSnackbar({ message: 'Order created successfully!', type: 'success' });
                setTimeout(() => navigate(-1), 800);
            }
        } catch (err: any) {
            setSnackbar({ message: err.message || 'Failed to create order', type: 'error' });
        } finally { setLoading(false); }
    };

    return (
        <div className="w-full pb-12 font-sans">

            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Create New Order</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 font-medium transition-colors">
                        Users
                    </button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">Create New Order</span>
                </nav>
            </div>

                <form onSubmit={handleSubmit} className="space-y-5">

                    {/* Order Details Card */}
                    <div className="sticky top-4 z-20 bg-white rounded-2xl border-2 border-black px-6 py-5 space-y-4 shadow-md">
                        <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Order Details</h4>
                        <div className="flex flex-wrap items-start gap-4">
                            <div className="space-y-1 w-56">
                                <label className="block text-xs font-medium text-slate-600 whitespace-nowrap">Customer Details</label>
                                <div className="min-h-[38px] px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-center gap-0.5 overflow-hidden">
                                    <span className="text-sm font-bold text-slate-800 truncate">{userName || '—'}</span>
                                    <MaskedMobile mobile={mobile} countryCode={userCountryCode} className="text-xs text-slate-500" />
                                </div>
                            </div>
                            <div className="space-y-1 w-96">
                                <label className="block text-xs font-medium text-slate-600">Breed <span className="text-red-500">*</span></label>
                                {loadingProducts ? <div className="h-9 bg-slate-100 rounded-lg animate-pulse" /> : (
                                    <Select
                                        value={breedId}
                                        onChange={v => {
                                            setBreedId(v);
                                            const p = products.find(x => x.id === v);
                                            if (p) setBaseUnitCost(p.price);
                                            updateError('breedId');
                                        }}
                                        options={products.map(p => ({ value: p.id, label: `${p.name} — ₹${p.price.toLocaleString('en-IN')} / unit` }))}
                                        className={`w-full pl-3 py-2 rounded-lg border text-sm bg-white ${errors.breedId ? 'border-red-300' : 'border-slate-200 focus:border-[#1B4332]'}`}
                                    />
                                )}
                                {errors.breedId && <p className="text-[10px] text-red-500">{errors.breedId}</p>}
                            </div>
                            <div className="space-y-1 w-36">
                                <label className="block text-xs font-medium text-slate-600 whitespace-nowrap">Number of Units <span className="text-red-500">*</span></label>
                                <input
                                    type="number" min={1} max={999} step={1}
                                    value={numUnits || ''}
                                    onKeyDown={e => { if (['.', '-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                                    onWheel={e => (e.target as HTMLInputElement).blur()}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === '') {
                                            setNumUnits('' as any);
                                        } else {
                                            const parsed = Math.min(999, Math.max(1, parseInt(val, 10)));
                                            setNumUnits(parsed);
                                        }
                                        updateError('numUnits');
                                    }}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.numUnits ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                />
                                {errors.numUnits && <p className="text-[10px] text-red-500">{errors.numUnits}</p>}
                            </div>
                            <div className="space-y-1 w-52">
                                <label className="block text-xs font-medium text-slate-600 whitespace-nowrap">Farm Location <span className="text-red-500">*</span></label>
                                {loadingFarms ? <div className="h-9 bg-slate-100 rounded-lg animate-pulse" /> : (
                                    <Select
                                        value={farmId}
                                        onChange={v => { setFarmId(v); updateError('farmId'); }}
                                        options={[
                                            { value: '', label: 'Select farm' },
                                            ...farms.map(f => ({ value: f.id, label: f.location || f.id })),
                                        ]}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm ${errors.farmId ? 'border-red-300' : 'border-slate-200 focus:border-[#1B4332]'}`}
                                    />
                                )}
                                {errors.farmId && <p className="text-[10px] text-red-500">{errors.farmId}</p>}
                            </div>
                            <div className="space-y-1 w-64">
                                <label className="block text-xs font-medium text-slate-600 whitespace-nowrap">Marketing Person (optional)</label>
                                {loadingMarketingStaff ? <div className="h-9 bg-slate-100 rounded-lg animate-pulse" /> : (
                                    <Select
                                        value={orderOnboardedByMobile}
                                        onChange={v => {
                                            setOrderOnboardedByMobile(v);
                                            updateError('orderOnboardedByMobile');
                                        }}
                                        options={[
                                            { value: '', label: 'None' },
                                            ...activeOnly(marketingStaff).map(m => personOption(m, { detail: m.role })),
                                        ]}
                                        renderValue={staffFilterValue(marketingStaff)}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm ${errors.orderOnboardedByMobile ? 'border-red-300' : 'border-slate-200 focus:border-[#1B4332]'}`}
                                    />
                                )}
                                {errors.orderOnboardedByMobile && <p className="text-[10px] text-red-500">{errors.orderOnboardedByMobile}</p>}
                            </div>
                            {breedId && (
                                <div className="space-y-1 w-48">
                                    <label className="block text-xs font-medium text-blue-600 whitespace-nowrap">Total Order Amount</label>
                                    <div className="h-[38px] px-4 bg-blue-50 border border-blue-100 rounded-lg flex items-center">
                                        <span className="text-sm font-bold text-blue-800">₹{totalCost.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Payment Details Card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col mb-5 divide-y divide-slate-200">
                        {/* Row 1: Payment Details & Admin Details */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 gap-4">
                            <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Payment Details</h4>
                            <div className="flex items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                <User size={12} className="text-slate-400" />
                                <span className="font-semibold text-slate-700">{adminName}</span>
                                <span className="text-slate-300">·</span>
                                <span>{adminRole}</span>
                                <span className="text-slate-300">·</span>
                                <span>{adminMobile}</span>
                                <span className="text-slate-300">·</span>
                                <span>{formatDateToDdmmyyyy(createdDate)}</span>
                            </div>
                        </div>

                        {/* Row 2: Payment Methods */}
                        <div className="p-6 space-y-1.5">
                            <label className="block text-xs font-medium text-slate-600">Payment Methods</label>
                            <div className="flex flex-wrap gap-2">
                                {PAYMENT_METHOD_CHIPS.map(({ key, label, icon }) => (
                                    <button key={key} type="button"
                                        onClick={() => { setPaymentMethod(key); resetPaymentSubFields(); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === key ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        {icon}{label}
                                    </button>
                                ))}
                                {!loadingCoins && availableCoins !== null && (
                                    <button type="button"
                                        onClick={() => { setPaymentMethod('COINS_REDEEM'); resetPaymentSubFields(); setCoinsToRedeem(String(Math.min(availableCoins ?? 0, totalCost))); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === 'COINS_REDEEM' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        <span className="text-base leading-none">🪙</span>Coins
                                    </button>
                                )}
                                {!loadingWallet && availableWallet !== null && (
                                    <button type="button"
                                        onClick={() => { setPaymentMethod('WALLET'); resetPaymentSubFields(); setWalletAmountToUse(String(Math.min(availableWallet ?? 0, totalCost))); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === 'WALLET' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        <Wallet size={15} />Wallet
                                    </button>
                                )}
                            </div>
                            {errors.paymentMethod && <p className="text-[10px] text-red-500">{errors.paymentMethod}</p>}
                        </div>

                        {/* Row 3: Image & Fields */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                            {/* Left Column: Image Preview */}
                            <div className="p-6 flex flex-col">
                                <div className="min-h-[300px] flex flex-col">
                                    {paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' ? (
                                        <ImagePreviewModal
                                            inline
                                            part="image"
                                            url={activeUpload.url || null}
                                            uploadLabel={activeUploadLabel}
                                            uploading={activeUpload.uploading}
                                            uploadProgress={activeUpload.progress}
                                            uploadErrorMsg={activeUpload.error || errors[activeUploadErrorKey]}
                                            onUpload={f => {
                                                if (activeUpload.url) {
                                                    clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode });
                                                }
                                                handleFileUpload(f, setActiveUpload, 'proof', activeUploadErrorKey);
                                            }}
                                            onUrlSubmit={url => {
                                                if (activeUpload.url) {
                                                    clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode });
                                                }
                                                handleUrlSubmit(url, setActiveUpload, activeUploadErrorKey);
                                            }}
                                            onClear={() => setActiveUpload(emptyUpload())}
                                            onImageZoom={setZoomUrl}
                                            secondaryUpload={paymentMethod === 'CHEQUE' ? {
                                                label: 'Cheque Back Image (Optional)',
                                                url: chequeBackUpload.url,
                                                uploading: chequeBackUpload.uploading,
                                                error: chequeBackUpload.error,
                                                onUpload: f => handleFileUpload(f, setChequeBackUpload, 'cheque_back', 'chequeBackUpload'),
                                                onUrlSubmit: url => handleUrlSubmit(url, setChequeBackUpload, 'chequeBackUpload'),
                                                onClear: () => setChequeBackUpload(emptyUpload()),
                                                onZoom: setZoomUrl,
                                            } : undefined}
                                        />
                                    ) : (
                                        <div className="flex-1 w-full h-full rounded-lg border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 bg-slate-50 min-h-[200px]">
                                            <span className="text-xs font-medium">Select a payment method</span>
                                            <span className="text-[10px]">to upload a document</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column: Form Fields & Extracted Text */}
                            <div className="flex flex-col h-full divide-y divide-slate-200">
                                {/* Top Row: Form Fields */}
                                <div className="p-6 flex-1">
                                    <div className="space-y-5">
                                        {paymentMethod === 'COINS_REDEEM' ? (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                                {(availableCoins ?? 0) <= 0 ? (
                                                    <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-500 text-center">
                                                        🪙 No coins available
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <div className="space-y-1 w-full">
                                                            <div className="flex items-center justify-between">
                                                                <label className="block text-sm font-medium text-slate-600">
                                                                    Coins <span className="text-black font-normal">(Available: {(availableCoins ?? 0).toLocaleString('en-IN')} coins)</span> <span className="text-red-500">*</span>
                                                                </label>
                                                                <button type="button"
                                                                    onClick={() => { setCoinsToRedeem(String(Math.min(availableCoins ?? 0, totalCost))); updateError('coinsToRedeem'); }}
                                                                    className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                                            </div>
                                                            <input
                                                                type="number" min={1}
                                                                value={coinsToRedeem}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                                onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                                                onChange={e => {
                                                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                    const numVal = raw === '' ? 0 : Number(raw);
                                                                    const avail = availableCoins ?? 0;
                                                                    if (raw !== '' && numVal > avail) return;
                                                                    setCoinsToRedeem(raw);
                                                                    updateError('coinsToRedeem');
                                                                }}
                                                                placeholder={`Enter coins (Available: ${(availableCoins ?? 0).toLocaleString('en-IN')})`}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.coinsToRedeem ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-amber-500'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                            />
                                                            {errors.coinsToRedeem && <p className="text-[10px] text-red-500">{errors.coinsToRedeem}</p>}
                                                            {Math.floor(Number(coinsToRedeem) || 0) > 0 && (
                                                                <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(Math.floor(Number(coinsToRedeem)))} Coins Only</p>
                                                            )}
                                                        </div>
                                                        {coinsFullyCover && (
                                                            <div className="rounded-lg px-3 py-2 text-xs font-bold bg-green-50 border border-green-200 text-green-700 text-center">
                                                                ✓ Order fully covered by coins!
                                                            </div>
                                                        )}
                                                        <div className="space-y-1 w-full pt-1">
                                                            <label className="block text-xs font-medium text-slate-600">Comment <span className="text-slate-400 font-normal">(optional)</span></label>
                                                            <input
                                                                type="text"
                                                                value={comment}
                                                                onChange={e => setComment(e.target.value)}
                                                                placeholder="Add an optional comment/note"
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1B4332]"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : paymentMethod === 'WALLET' ? (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                                {(availableWallet ?? 0) <= 0 ? (
                                                    <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-500 text-center">
                                                        💼 No wallet balance available
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <div className="space-y-1 w-full">
                                                            <div className="flex items-center justify-between">
                                                                <label className="block text-sm font-medium text-slate-600">
                                                                    Wallet Amount <span className="text-black font-normal">(Available: ₹{(availableWallet ?? 0).toLocaleString('en-IN')})</span> <span className="text-red-500">*</span>
                                                                </label>
                                                                <button type="button"
                                                                    onClick={() => { setWalletAmountToUse(String(Math.min(availableWallet ?? 0, totalCost))); updateError('walletAmountToUse'); }}
                                                                    className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                                            </div>
                                                            <input
                                                                type="number" min={1}
                                                                value={walletAmountToUse}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                                onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                                                onChange={e => {
                                                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                    const numVal = raw === '' ? 0 : Number(raw);
                                                                    const avail = availableWallet ?? 0;
                                                                    if (raw !== '' && numVal > avail) return;
                                                                    setWalletAmountToUse(raw);
                                                                    updateError('walletAmountToUse');
                                                                }}
                                                                placeholder={`Enter wallet amount (Available: ₹${(availableWallet ?? 0).toLocaleString('en-IN')})`}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.walletAmountToUse ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                            />
                                                            {errors.walletAmountToUse && <p className="text-[10px] text-red-500">{errors.walletAmountToUse}</p>}
                                                            {Math.floor(Number(walletAmountToUse) || 0) > 0 && (
                                                                <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(Math.floor(Number(walletAmountToUse)))} Rupees Only</p>
                                                            )}
                                                        </div>
                                                        <div className="space-y-1 w-full pt-1">
                                                            <label className="block text-xs font-medium text-slate-600">Comment <span className="text-slate-400 font-normal">(optional)</span></label>
                                                            <input
                                                                type="text"
                                                                value={comment}
                                                                onChange={e => setComment(e.target.value)}
                                                                placeholder="Add an optional comment/note"
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1B4332]"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : paymentMethod && paymentMethod !== 'NONE' && (
                                            <ImagePreviewModal
                                                inline
                                                part="fields"
                                                showSurplus
                                                surplusAmount={surplusAmount}
                                                onSurplusChange={setSurplusAmount}
                                                surplusComment={surplusComment}
                                                onSurplusCommentChange={setSurplusComment}
                                                comment={comment}
                                                onCommentChange={setComment}
                                                url={activeUpload.url || null} text={ocrText} loading={ocrLoading}
                                                activePaymentType={paymentMethod}
                                                remaining={remainingAfterCoins || totalCost}
                                                fieldErrors={errors}
                                                onUtrDuplicate={setUtrDuplicate}
                                                onAutoFill={values => applyAutoFillValues(
                                                    values,
                                                    { setTxAmount, setTxDate, setTransferMode, setPaidBy, setUtrNumber, setAccountHolderName, setBankName, setCashierName, setCashierPhone },
                                                )}
                                                onFieldVerified={key => setVerifiedFields(prev => new Set(prev).add(key))}
                                                onFieldEdited={onFieldEdited}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Bottom Row: Extracted Text */}
                                {paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' && (
                                    <div className="p-6 bg-slate-50/30">
                                        <ImagePreviewModal
                                            inline
                                            part="text"
                                            url={activeUpload.url || null} text={ocrText} loading={ocrLoading}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button type="button" onClick={() => navigate(-1)}
                            className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-semibold text-sm transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading || anyUploading || !canCreateOrder || !allFieldsVerified || utrDuplicate || (paymentMethod === 'COINS_REDEEM' && (availableCoins ?? 0) <= 0) || (paymentMethod === 'WALLET' && (availableWallet ?? 0) <= 0)}
                            title={!canCreateOrder ? 'You do not have permission to create orders' : !allFieldsVerified ? 'Verify every field before creating the order' : (utrDuplicate ? 'This transaction number already exists' : (paymentMethod === 'COINS_REDEEM' && (availableCoins ?? 0) <= 0) ? 'No coins available to redeem' : (paymentMethod === 'WALLET' && (availableWallet ?? 0) <= 0) ? 'No wallet balance available to redeem' : undefined)}
                            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors shadow-sm">
                            {loading ? <><Loader2 size={15} className="animate-spin" /> Creating...</>
                                : anyUploading ? <><Loader2 size={15} className="animate-spin" /> Uploading...</>
                                    : <><ShoppingCart size={15} /> Create Order</>}
                        </button>
                    </div>

                </form>

            {snackbar && <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />}
            <ImageViewerModal url={zoomUrl} onClose={() => setZoomUrl(null)} />
        </div>
    );
};

export default UserCreateOrderPage;
